import { useEffect, useRef, useState } from 'react';
import { FaPencilAlt } from 'react-icons/fa';

/**
 * Inline editable group conversation name.
 * Shows a pencil icon on hover and allows editing when clicked.
 * Only admins/owners can edit (controlled by parent).
 */
export function EditableGroupName({ name, onSave, canEdit }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(name);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);

  // Reset edit value when name prop changes
  useEffect(() => {
    setEditValue(name);
  }, [name]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleStartEdit = () => {
    if (!canEdit) return;
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditValue(name);
    setIsEditing(false);
  };

  const handleSave = async () => {
    const trimmedValue = editValue.trim();

    // Don't save if empty or unchanged
    if (!trimmedValue || trimmedValue === name) {
      handleCancel();
      return;
    }

    setIsSaving(true);
    try {
      await onSave(trimmedValue);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update group name:', error);
      // Keep in edit mode on error so user can try again
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleBlur = () => {
    // Auto-save on blur
    if (!isSaving) {
      handleSave();
    }
  };

  if (!isEditing) {
    return (
      <div className="responder-messenger-editable-name">
        <h6 className="mb-0">{name}</h6>
        {canEdit && (
          <button
            type="button"
            className="responder-messenger-editable-name__edit-btn"
            onClick={handleStartEdit}
            aria-label="Edit group name"
            title="Edit group name"
          >
            <FaPencilAlt size={10} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="responder-messenger-editable-name responder-messenger-editable-name--editing">
      <input
        ref={inputRef}
        type="text"
        className="responder-messenger-editable-name__input"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        disabled={isSaving}
        maxLength={100}
        placeholder="Group name"
      />
    </div>
  );
}
